// HT-2 · 学習候補の詳細（決まった順番で見せる） + 確認者の操作
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { LEARN, API, authHeader, detailOf, Pill, Collapse, ROLE_JA } from './shared';

const REGION_ROLES = [
  ['', '指定なし'], ['title_block', 'タイトル欄'], ['responsibility_area', '責任者欄'],
  ['stamp', '押印欄'], ['company_seal', '社印欄'], ['revision_block', '改訂表'],
  ['drawing_area', '図面本体'], ['table', '表'], ['notes', '注記'],
];

const Section = ({ n, title, children, testId }) => (
  <section className="border-t border-zinc-100 pt-3" data-testid={testId}>
    <h4 className="text-[12.5px] font-semibold text-zinc-900">
      <span className="pr-1.5 tabular-nums text-zinc-400">{n}</span>{title}
    </h4>
    <div className="pt-1.5">{children}</div>
  </section>
);

const ExampleList = ({ rows, tone, onRemove, testId }) => (
  rows.length === 0 ? (
    <p className="text-[11.5px] text-zinc-500" data-testid={`${testId}-empty`}>
      登録されていません。
    </p>
  ) : (
    <ul className="space-y-1" data-testid={testId}>
      {rows.map((r) => (
        <li key={r.example_id}
          className="rounded-md border border-zinc-200 px-2.5 py-1.5 text-[11.5px] text-zinc-800"
          data-testid={`${testId}-${r.example_id}`}>
          <span className="flex flex-wrap items-center gap-1.5">
            <Pill tone={tone}>{r.polarity === 'positive' ? '正しい例' : '誤りやすい例'}</Pill>
            <span>読み取り: {r.prior_result || '—'} → {r.corrected_result || '（採用しない）'}</span>
            {r.locator?.page && <Pill>ページ {r.locator.page}</Pill>}
            {r.confirmation_status && <Pill>{r.confirmation_status}</Pill>}
          </span>
          <span className="flex flex-wrap items-center gap-2 pt-1 text-[11px] text-zinc-500">
            {r.document_id && <span>書類: {r.document_id}</span>}
            {r.source_content_hash && <span>内容ハッシュ: {String(r.source_content_hash).slice(0, 10)}</span>}
            {r.reviewer && <span>登録者: {r.reviewer}</span>}
            {r.note && <span>{r.note}</span>}
            {onRemove && !String(r.example_id).startsWith('legacy') && (
              <button type="button" onClick={() => onRemove(r.example_id)}
                className="min-h-[28px] text-teal-700 hover:underline"
                data-testid={`${testId}-remove-${r.example_id}`}>この例を外す</button>
            )}
          </span>
        </li>
      ))}
    </ul>
  )
);

export default function ProposalDetail({ id, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('');           // '', reject, examples, scope, edit
  const [reason, setReason] = useState('wrong_reading');
  const [note, setNote] = useState('');
  const [scope, setScope] = useState(null);
  const [justify, setJustify] = useState('');
  const [edit, setEdit] = useState({ corrected_value: '', risk: '', risk_reason: '' });
  const [ex, setEx] = useState({ polarity: 'negative', prior_result: '', corrected_result: '', note: '' });
  const [ack, setAck] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${LEARN}/proposals/${id}/detail`, { headers: authHeader() });
      setD(r.data);
      setScope(r.data.sections.scope.scope);
      setJustify(r.data.sections.scope.justification || '');
    } catch (e) { toast.error(detailOf(e, '内容を取得できませんでした')); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/api/doc-ai/profiles`, { headers: authHeader() })
      .then((r) => setProfiles(r.data?.rows || r.data?.profiles || []))
      .catch(() => setProfiles([]));
  }, []);

  const call = async (fn, okMessage) => {
    setBusy(true);
    try {
      await fn();
      if (okMessage) toast.success(okMessage);
      await load();
      onChanged?.();
      setMode('');
      setNote('');
      return true;
    } catch (e) {
      toast.error(detailOf(e, '処理できませんでした'));
      return false;
    } finally { setBusy(false); }
  };

  if (!d) return null;
  const S = d.sections;
  const risk = S.risk;
  const decided = d.status === 'approved' || d.status === 'rejected';
  const post = (path, body) => axios.post(`${LEARN}${path}`, body, { headers: authHeader() });

  const approve = (edits) => call(async () => {
    const fresh = (await axios.get(`${LEARN}/proposals/${id}/detail`,
      { headers: authHeader() })).data;
    const r = await post(`/proposals/${id}/approve`, {
      expected_revision: fresh.revision, accept_scope: true,
      reviewed_risk: fresh.sections.risk.risk, edits: edits || null, note,
    });
    toast.success(r.data?.notice_ja || '承認しました');
    onClose?.();
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-zinc-900/40 p-3 sm:p-6"
      role="dialog" aria-modal="true" aria-label="学習候補の内容"
      data-testid="proposal-detail">
      <div className="w-full max-w-3xl rounded-lg border border-zinc-200 bg-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-zinc-900"
              data-testid="proposal-detail-title">{S.proposal.text_ja}</h3>
            <p className="flex flex-wrap gap-1.5 pt-1.5">
              <Pill>{d.category_label_ja}</Pill>
              <Pill tone={risk.risk === 'high' ? 'rose' : risk.risk === 'medium' ? 'amber' : 'teal'}
                testId="proposal-detail-risk">影響の大きさ: {risk.size_ja}</Pill>
              <Pill tone={d.status === 'approved' ? 'teal' : d.status === 'rejected' ? 'rose' : 'zinc'}>
                {d.status_label_ja}
              </Pill>
              {S.conflicts.blocking.length > 0 && (
                <Pill tone="rose" testId="proposal-detail-conflict">不一致あり</Pill>
              )}
            </p>
          </div>
          <Button variant="outline" className="h-9 text-[12px]" onClick={onClose}
            data-testid="proposal-detail-close">閉じる</Button>
        </div>

        <p className="mt-3 rounded-md border border-teal-100 bg-teal-50/60 px-3 py-2 text-[11.5px] leading-relaxed text-zinc-700"
          data-testid="proposal-detail-notice">{d.notice_ja}</p>

        <div className="space-y-3 pt-3">
          <Section n={1} title={S.proposal.title_ja} testId="proposal-section-proposal">
            <p className="text-[12px] text-zinc-800">{S.proposal.text_ja}</p>
            <p className="pt-1 text-[11.5px] text-zinc-600">{S.proposal.impact_ja}</p>
          </Section>

          <Section n={2} title={S.source.title_ja} testId="proposal-section-source">
            <p className="text-[11.5px] text-zinc-800">
              元の読み取り: {S.source.observed_value || '—'} → 修正: {S.source.corrected_value || '—'}
            </p>
            <p className="text-[11.5px] text-zinc-600">
              元になった書類 {S.source.document_count}件 · 支えとなる修正 {S.source.correction_count}件 ·
              提出者 {S.source.submitted_by || '—'}
            </p>
            {(S.source.documents || []).map((doc) => (
              <p key={doc.id} className="text-[11.5px] text-zinc-700">書類: {doc.title || doc.id}</p>
            ))}
            {(S.source.human_explanations || []).map((t) => (
              <p key={t} className="pt-1 text-[11.5px] italic text-zinc-700">「{t}」</p>
            ))}
          </Section>

          <Section n={3} title={S.positive.title_ja} testId="proposal-section-positive">
            <ExampleList rows={S.positive.rows} tone="teal" testId="positive-examples"
              onRemove={decided ? null
                : (eid) => call(() => axios.delete(`${LEARN}/proposals/${id}/examples/${eid}`,
                  { headers: authHeader() }), '例を外しました')} />
          </Section>

          <Section n={4} title={S.negative.title_ja} testId="proposal-section-negative">
            <ExampleList rows={S.negative.rows} tone="amber" testId="negative-examples"
              onRemove={decided ? null
                : (eid) => call(() => axios.delete(`${LEARN}/proposals/${id}/examples/${eid}`,
                  { headers: authHeader() }), '例を外しました')} />
            {!decided && (
              <button type="button" onClick={() => setMode(mode === 'example' ? '' : 'example')}
                className="mt-1.5 min-h-[32px] text-[11.5px] text-teal-700 hover:underline"
                data-testid="proposal-add-example">例を追加する</button>
            )}
            {mode === 'example' && !decided && (
              <div className="mt-1.5 space-y-1.5 rounded-md border border-zinc-200 p-2.5">
                <select value={ex.polarity} aria-label="例の種類"
                  onChange={(e) => setEx({ ...ex, polarity: e.target.value })}
                  className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
                  data-testid="example-polarity">
                  <option value="positive">正しい例</option>
                  <option value="negative">誤って適用してはいけない例</option>
                </select>
                <input value={ex.prior_result} placeholder="読み取られた文字"
                  aria-label="読み取られた文字"
                  onChange={(e) => setEx({ ...ex, prior_result: e.target.value })}
                  className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
                  data-testid="example-prior" />
                <input value={ex.corrected_result} placeholder="正しい内容（誤りやすい例では空欄）"
                  aria-label="正しい内容"
                  onChange={(e) => setEx({ ...ex, corrected_result: e.target.value })}
                  className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
                  data-testid="example-corrected" />
                <input value={ex.note} placeholder="説明（任意）" aria-label="例の説明"
                  onChange={(e) => setEx({ ...ex, note: e.target.value })}
                  className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
                  data-testid="example-note" />
                <Button className="h-9 bg-teal-700 text-[12px] hover:bg-teal-800" disabled={busy}
                  onClick={() => call(async () => {
                    await post(`/proposals/${id}/examples`, ex);
                    setEx({ polarity: 'negative', prior_result: '', corrected_result: '', note: '' });
                  }, '例を追加しました')}
                  data-testid="example-save">この例を追加</Button>
              </div>
            )}
          </Section>

          <Section n={5} title={S.scope.title_ja} testId="proposal-section-scope">
            <p className="flex flex-wrap gap-1.5">
              <Pill tone={S.scope.breadth === 'broad' ? 'rose' : S.scope.breadth === 'wide' ? 'amber' : 'teal'}
                testId="proposal-scope-breadth">範囲: {S.scope.breadth_label_ja}</Pill>
              {S.scope.is_recommended && <Pill tone="teal">推奨どおり</Pill>}
              {(S.scope.real_document_validation || []).map((v) => (
                <Pill key={v.profile_key} tone={v.real_document_validated ? 'teal' : 'amber'}>
                  {v.label_ja}
                </Pill>
              ))}
            </p>
            <dl className="grid gap-x-4 pt-1.5 sm:grid-cols-2">
              {S.scope.rows.filter((r) => r.value_ja !== '指定なし').map((r) => (
                <div key={r.key} className="flex gap-2 py-0.5">
                  <dt className="text-[11.5px] text-zinc-500">{r.label_ja}</dt>
                  <dd className="text-[11.5px] text-zinc-800">{r.value_ja}</dd>
                </div>
              ))}
            </dl>
            {(S.scope.warnings_ja || []).map((w) => (
              <p key={w} className="pt-1 text-[11.5px] text-amber-900"
                data-testid="proposal-scope-warning">{w}</p>
            ))}
            {S.scope.justification && (
              <p className="pt-1 text-[11.5px] text-zinc-600">範囲の理由: {S.scope.justification}</p>
            )}
          </Section>

          <Section n={6} title={risk.title_ja} testId="proposal-section-risk">
            <p className="text-[12px] text-zinc-900">影響の大きさ: {risk.size_ja}</p>
            <ul className="list-disc pl-5 pt-1 text-[11.5px] text-zinc-700">
              {risk.reasons_ja.map((r) => <li key={r}>{r}</li>)}
            </ul>
          </Section>

          <Section n={7} title={S.conflicts.title_ja} testId="proposal-section-conflicts">
            {S.conflicts.rows.length === 0 ? (
              <p className="text-[11.5px] text-zinc-500">不一致はありません。</p>
            ) : (
              <ul className="space-y-1">
                {S.conflicts.rows.map((c) => (
                  <li key={c.conflict_id}
                    className={`rounded-md border px-2.5 py-1.5 text-[11.5px] ${
                      c.blocking && !c.resolved ? 'border-rose-200 bg-rose-50/60 text-rose-900'
                        : 'border-zinc-200 text-zinc-700'}`}
                    data-testid={`proposal-conflict-${c.type}`}>
                    <span className="font-medium">{c.label_ja}</span>
                    <span className="pl-1">{c.detail_ja}</span>
                    {c.resolved && <span className="pl-1 text-teal-700">（解消済み）</span>}
                    {c.code_owned && <span className="pl-1">（安全規則のため解消できません）</span>}
                    {c.resolvable && !c.resolved && (
                      <button type="button"
                        onClick={() => call(() => post(
                          `/proposals/${id}/conflicts/${c.conflict_id}/resolve`,
                          { note: note || 'この候補を優先します' }), '不一致を解消しました')}
                        className="pl-2 min-h-[28px] text-teal-700 hover:underline"
                        data-testid={`proposal-conflict-resolve-${c.type}`}>この不一致を解消する</button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section n={8} title={S.after_approval.title_ja} testId="proposal-section-after">
            <p className="text-[11.5px] leading-relaxed text-zinc-800">{S.after_approval.text_ja}</p>
            <ul className="list-disc pl-5 pt-1 text-[11.5px] text-zinc-700">
              {S.after_approval.changes_now_ja.map((t) => <li key={t}>{t}</li>)}
            </ul>
          </Section>

          <Collapse label="9 技術情報" testId="proposal-section-technical">
            <p className="break-all text-[11px] text-zinc-600">
              種類: {S.technical.kind} · 項目: {ROLE_JA[S.technical.field_key] || S.technical.field_key || '—'} ·
              個人・部署などの種類: {S.technical.entity_type || '—'} ·
              規則: {JSON.stringify(S.technical.proposed_rule || {})} ·
              教示セッション: {S.technical.teaching_session_id || '—'} ·
              版: {d.revision}{S.technical.snapshot_id ? ` · 承認記録: ${S.technical.snapshot_id}` : ''}
            </p>
            {(S.technical.revisions || []).map((r) => (
              <p key={`${r.revision}-${r.at}`} className="pt-1 text-[11px] text-zinc-500">
                版 {r.revision}: {r.what} · {r.by} · {r.at}
              </p>
            ))}
          </Collapse>
        </div>

        {!decided && (
          <div className="mt-4 border-t border-zinc-200 pt-3">
            <p className="text-[11.5px] text-zinc-700" data-testid="proposal-next-action">
              次にすること: {d.gate.next_action_ja}
            </p>
            {!d.gate.can_approve && d.gate.missing_ja.length > 0 && (
              <ul className="list-disc pl-5 pt-1 text-[11.5px] text-amber-900"
                data-testid="proposal-gate-missing">
                {d.gate.missing_ja.map((m) => <li key={m}>{m}</li>)}
              </ul>
            )}
            <label className="flex items-center gap-2 pt-2 text-[11.5px] text-zinc-800">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                className="h-4 w-4" data-testid="proposal-ack" />
              適用範囲（{S.scope.breadth_label_ja}）と影響の大きさ（{risk.size_ja}）を確認しました
            </label>
            <div className="flex flex-wrap gap-1.5 pt-2">
              <Button className="h-10 bg-teal-700 text-[12.5px] hover:bg-teal-800"
                disabled={!d.gate.can_approve || !ack || busy}
                onClick={() => approve(null)} data-testid="proposal-approve">承認</Button>
              <Button variant="outline" className="h-10 text-[12px]" disabled={busy || !ack}
                onClick={() => setMode(mode === 'edit' ? '' : 'edit')}
                data-testid="proposal-approve-edit">修正して承認</Button>
              <Button variant="outline" className="h-10 text-[12px]" disabled={busy}
                onClick={() => setMode(mode === 'reject' ? '' : 'reject')}
                data-testid="proposal-reject">却下</Button>
              <Button variant="outline" className="h-10 text-[12px]" disabled={busy}
                onClick={() => setMode(mode === 'examples' ? '' : 'examples')}
                data-testid="proposal-request-examples">追加例を求める</Button>
              <Button variant="outline" className="h-10 text-[12px]" disabled={busy}
                onClick={() => call(() => post(`/proposals/${id}/defer`, { note }), '「後で確認」にしました')}
                data-testid="proposal-defer">後で確認</Button>
              <Button variant="outline" className="h-10 text-[12px]" disabled={busy}
                onClick={() => setMode(mode === 'scope' ? '' : 'scope')}
                data-testid="proposal-change-scope">適用範囲を変更</Button>
              {d.group && (
                <Button variant="outline" className="h-10 text-[12px]" disabled={busy}
                  onClick={() => call(() => post(`/proposal-groups/${d.group.id}/split`,
                    { member_ids: [id] }), 'この候補をグループから分けました')}
                  data-testid="proposal-split">候補を分割</Button>
              )}
            </div>

            {mode === 'reject' && (
              <div className="mt-2 space-y-1.5 rounded-md border border-zinc-200 p-2.5"
                data-testid="proposal-reject-form">
                <select value={reason} onChange={(e) => setReason(e.target.value)}
                  aria-label="却下の理由"
                  className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
                  data-testid="reject-reason">
                  {d.rejection_reasons.map((r) => (
                    <option key={r.key} value={r.key}>{r.label_ja}</option>
                  ))}
                </select>
                <input value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="補足（任意）" aria-label="却下の補足"
                  className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
                  data-testid="reject-note" />
                <Button className="h-9 bg-teal-700 text-[12px] hover:bg-teal-800" disabled={busy}
                  onClick={() => call(async () => {
                    await post(`/proposals/${id}/reject`, { reason_code: reason, note });
                    onClose?.();
                  }, '却下しました')}
                  data-testid="reject-save">この理由で却下</Button>
              </div>
            )}

            {mode === 'examples' && (
              <div className="mt-2 space-y-1.5 rounded-md border border-zinc-200 p-2.5"
                data-testid="proposal-examples-form">
                <p className="text-[11.5px] text-zinc-700">必要な例を伝えます（状態は「追加例が必要」になります）。</p>
                <input value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="どんな例が必要か" aria-label="必要な例"
                  className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
                  data-testid="request-examples-note" />
                <div className="flex flex-wrap gap-1.5">
                  {[['positive', '正しい例'], ['negative', '誤りやすい例']].map(([k, label]) => (
                    <Button key={k} variant="outline" className="h-9 text-[12px]" disabled={busy}
                      onClick={() => call(() => post(`/proposals/${id}/request-examples`, {
                        requests: [{ kind: k, profile_key: (S.scope.scope.profile_keys || [])[0], note }],
                        note,
                      }), '追加例を求めました')}
                      data-testid={`request-examples-${k}`}>{label}を求める</Button>
                  ))}
                </div>
              </div>
            )}

            {mode === 'scope' && scope && (
              <div className="mt-2 space-y-1.5 rounded-md border border-zinc-200 p-2.5"
                data-testid="proposal-scope-form">
                <p className="text-[11.5px] text-zinc-700">できるだけ狭い範囲を選んでください。</p>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {(profiles.length ? profiles : (scope.profile_keys || []).map((k) => ({ key: k, label_ja: k })))
                    .map((p) => (
                      <label key={p.key} className="flex items-center gap-1.5 text-[11.5px] text-zinc-800">
                        <input type="checkbox" className="h-4 w-4"
                          checked={(scope.profile_keys || []).includes(p.key)}
                          onChange={(e) => setScope({
                            ...scope,
                            profile_keys: e.target.checked
                              ? [...(scope.profile_keys || []), p.key]
                              : (scope.profile_keys || []).filter((k) => k !== p.key),
                          })}
                          data-testid={`scope-profile-${p.key}`} />
                        {p.label_ja || p.key}
                      </label>
                    ))}
                </div>
                <select value={scope.region_role || ''} aria-label="領域の種類"
                  onChange={(e) => setScope({ ...scope, region_role: e.target.value || null })}
                  className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
                  data-testid="scope-region-role">
                  {REGION_ROLES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
                <input value={scope.source_label || ''} placeholder="印字されたラベル（例: DESIGNED）"
                  aria-label="印字されたラベル"
                  onChange={(e) => setScope({ ...scope, source_label: e.target.value || null })}
                  className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
                  data-testid="scope-source-label" />
                <input value={justify} onChange={(e) => setJustify(e.target.value)}
                  placeholder="範囲を広くする理由（広い範囲では必須）" aria-label="範囲の理由"
                  className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
                  data-testid="scope-justification" />
                <Button className="h-9 bg-teal-700 text-[12px] hover:bg-teal-800" disabled={busy}
                  onClick={() => call(() => post(`/proposals/${id}/scope`,
                    { scope, justification: justify }), '適用範囲を変更しました')}
                  data-testid="scope-save">この範囲にする</Button>
              </div>
            )}

            {mode === 'edit' && (
              <div className="mt-2 space-y-1.5 rounded-md border border-zinc-200 p-2.5"
                data-testid="proposal-edit-form">
                <input value={edit.corrected_value} placeholder="正しい内容に直す"
                  aria-label="正しい内容"
                  onChange={(e) => setEdit({ ...edit, corrected_value: e.target.value })}
                  className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
                  data-testid="edit-corrected-value" />
                <select value={edit.risk} aria-label="影響の大きさを上げる"
                  onChange={(e) => setEdit({ ...edit, risk: e.target.value })}
                  className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
                  data-testid="edit-risk">
                  <option value="">影響の大きさはそのまま</option>
                  <option value="medium">中に上げる</option>
                  <option value="high">大に上げる</option>
                </select>
                <input value={edit.risk_reason} placeholder="変更の理由" aria-label="変更の理由"
                  onChange={(e) => setEdit({ ...edit, risk_reason: e.target.value })}
                  className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
                  data-testid="edit-risk-reason" />
                <p className="text-[11px] text-zinc-500">
                  提出された内容は残り、変更は履歴として記録されます。
                </p>
                <Button className="h-9 bg-teal-700 text-[12px] hover:bg-teal-800" disabled={busy}
                  onClick={async () => {
                    if (edit.risk) {
                      const ok = await call(() => post(`/proposals/${id}/risk`,
                        { risk: edit.risk, reason: edit.risk_reason || '確認者が変更しました' }));
                      if (!ok) return;
                    }
                    await approve({ corrected_value: edit.corrected_value || null });
                  }}
                  data-testid="edit-approve-save">修正して承認</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
